import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Alert, CircularProgress,
} from '@mui/material';
import { nodeApiPath } from '../utils/paths';

/**
 * Detalle de versiones, para soporte.
 *
 * El usuario ve "v1.1" en el pie del menú; acá está el dato que nos sirve a nosotros:
 * el commit exacto de cada lado y la fecha del build.
 *
 * Lo importante es detectar que el navegador tiene un bundle viejo cacheado: alguien
 * puede estar corriendo una copia de hace días sin saberlo, y reportar como bug algo que
 * ya está arreglado. Se compara el id de build incrustado en ESTA copia contra el que
 * publica el servidor; si difieren, el diálogo lo dice y explica cómo resolverlo.
 */
export default function AcercaDeDialog({ open, onClose, versionFront }) {
    const [back, setBack] = useState(null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCargando(true);
        fetch(nodeApiPath('health'))
            .then(r => r.json())
            .then(setBack)
            .catch(() => setBack(null))
            .finally(() => setCargando(false));
    }, [open]);

    const fmtFecha = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return isNaN(d) ? '—' : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    };

    // El bundle que el navegador tiene cargado lleva grabado su propio id de build;
    // `versionFront` viene de pedirle version.json al servidor recien. Si difieren, esta
    // copia es vieja — y esto NO depende de que alguien se haya acordado de subir la
    // version en package.json, que era la debilidad de la primera version de esto.
    const idDelBundle = process.env.REACT_APP_BUILD_ID;
    const idDelServidor = versionFront?.buildId;
    const bundleViejo = !!idDelBundle && !!idDelServidor && idDelBundle !== idDelServidor;

    // Caso distinto y mas raro: front y back desplegados en momentos distintos. Es un
    // problema de deploy, no del navegador del usuario.
    const deployDesparejo = back?.version && versionFront?.version
        && back.version !== versionFront.version;

    const Fila = ({ etiqueta, valor, detalle }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, py: 0.75 }}>
            <Box sx={{ color: 'text.secondary' }}>{etiqueta}</Box>
            <Box sx={{ textAlign: 'right', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {valor}
                {detalle && <Box sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{detalle}</Box>}
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ fontWeight: 600 }}>Acerca de SIGIF</DialogTitle>
            <DialogContent dividers>
                {cargando ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {/* Sin version.json la app corre con `npm start`, que no ejecuta el
                            prebuild: es lo normal en una maquina de desarrollo. */}
                        <Fila
                            etiqueta="Aplicación"
                            valor={versionFront?.version ? `v${versionFront.version}` : 'modo desarrollo'}
                            detalle={versionFront?.commit ? `${versionFront.commit} · ${fmtFecha(versionFront.fecha)}` : 'sin compilar'}
                        />
                        {/* El backend SIEMPRE informa version (la lee del package.json), asi
                            que si falta es que esta corriendo codigo viejo — no que sea un
                            entorno de desarrollo. Por eso el texto no dice "desarrollo". */}
                        <Fila
                            etiqueta="Servidor"
                            valor={back?.version ? `v${back.version}` : (back ? 'sin versión' : 'sin conexión')}
                            detalle={back
                                ? (back.version
                                    ? (back.commit && back.commit !== 'dev' ? back.commit.substring(0, 7) : back.environment)
                                    : 'código viejo: reiniciar el backend')
                                : null}
                        />
                        <Fila
                            etiqueta="Base de datos"
                            valor={back?.database === 'connected' ? 'conectada' : (back ? 'sin conexión' : '—')}
                        />

                        {bundleViejo && (
                            <Alert severity="warning" sx={{ mt: 2, borderRadius: '4px' }}>
                                Estás usando una copia vieja del sistema, guardada por el
                                navegador. Puede que algo no funcione o que no veas cambios
                                recientes.
                                <Box sx={{ mt: 1 }}>
                                    Recargá con <b>Ctrl+F5</b> para traer la última.
                                </Box>
                            </Alert>
                        )}

                        {!bundleViejo && deployDesparejo && (
                            <Alert severity="info" sx={{ mt: 2, borderRadius: '4px' }}>
                                La aplicación (<b>v{versionFront.version}</b>) y el servidor
                                (<b>v{back.version}</b>) están en versiones distintas. Tu copia
                                está al día; avisale a Informática.
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
